/* eslint-disable no-console */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import {
  FilePickerButton,
  HStack,
  Progress,
  useShowMessage,
} from '../../core-ui';
import { JsonType } from '../../core/constants/MimeTypes';
import { getTypeList } from '../FormBuilder/FormElements/FileUploadElement/ControlledFormView';
import FileUpload, { Response } from '../../support/FileUpload';

const acceptedFileTypes = getTypeList(JsonType);

export function ManualPaymentImportButton(props: {
  accountId: string;
  seasonId: string;
}) {
  const { accountId, seasonId } = props;
  const [upload, setUpload] = useState<FileUpload | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const showMessage = useShowMessage();
  const { mutate: doFix } = useMutation(
    async (files: Array<File>) => {
      let file = files[0];
      if (file) {
        attemptFileUpload({
          accountId,
          file,
          seasonId,
          setUpload,
          setUploadProgress,
          showMessage,
        });
      }

      showMessage(
        'success',
        t('Restoring manual payments. Check console for details.'),
      );
    },
    {
      onSuccess: () => {
        showMessage('success', t('Fixed successfully'));
      },
      onError: (error) => {
        console.error(error);
        showMessage('error', t('Error fixing: {error}', { error }));
      },
    },
  );

  return (
    <HStack>
      {upload ? (
        <Progress
          value={Math.round(uploadProgress * 1000) / 10}
          hasStripe
          style={{
            width: 210,
            marginRight: 8,
          }}
        />
      ) : (
        <FilePickerButton
          variant="outline"
          onFileSelect={(files: Array<File>) => doFix(files)}
          inputProps={{
            accept: acceptedFileTypes,
          }}
        >
          {t('Import Manual Payments')}
        </FilePickerButton>
      )}
    </HStack>
  );
}

function attemptFileUpload(input: {
  accountId: string;
  file: File;
  seasonId: string;
  setUpload: (upload: FileUpload | null) => void;
  setUploadProgress: (progress: number) => void;
  showMessage: ReturnType<typeof useShowMessage>;
}) {
  const {
    accountId,
    file,
    seasonId,
    setUpload,
    setUploadProgress,
    showMessage,
  } = input;
  let isValidFileType = file.type in JsonType;
  if (!isValidFileType) {
    showMessage(
      'error',
      t('Error: Invalid file type. Please select a JSON file.'),
    );
    return;
  }
  const numberOfBytesInMb = 1024 * 1024;
  const maxFileSize = 40 * numberOfBytesInMb;
  if (file.size > maxFileSize) {
    showMessage('error', t('Error: File exceeds maximum size.'));
    return;
  }
  let newUpload = new FileUpload(
    `/api/manual-payments/${accountId}/${seasonId}/fix`,
    file,
    { errorEarly: true },
  );
  const onUploadProgress = (loaded: number, total: number) => {
    let progress = loaded / total;
    console.log('progress', progress);
    setUploadProgress(progress);
  };
  newUpload.addListener('progress', onUploadProgress);
  const onUploadComplete = (error: Error | null, response: Response) => {
    if (error) {
      const message = t('Upload failed: {message}.', {
        message: error.message,
      });
      showMessage('error', message);
      console.error(message, error);
    } else if (response.body && response.body.error) {
      showMessage('error', response.body.error);
      console.error(response.body.error);
    } else if (response.status !== 200) {
      const message = t('Upload failed: HTTP Status {status}', {
        status: response.status,
      });
      showMessage('error', message);
      console.error(message);
    }
    setUpload(null);
  };
  newUpload.addListener('complete', onUploadComplete);
  setUpload(newUpload);
  setUploadProgress(0);
  newUpload.send();
}
